import * as Api from "@/api";
import EditorForm from "@/design/components/EditorForm";
import SaveIndicator from "@/design/components/SaveIndicator";
import InputPhone from "@/design/forms/InputPhone";
import InputSelect from "@/design/forms/InputSelect";
import InputText from "@/design/forms/InputText";
import InputToggle from "@/design/forms/InputToggle";
import { string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";

import PropTypes from "prop-types";

import ruleReducer from "./ruleReducer";

function RuleForm({ blocklist, activeRule, blocklistDispatch, setActiveRule }) {
  const isPbxBlocklist = blocklist.type === "pbx";

  // Form
  const { value, dispatch, setSaveState, formState, errors, allErrors, save } = useSave({
    initialValue: activeRule,
    reducer: ruleReducer,
    validator: () => ({
      prefix: string()
        .required({ message: "Prefix is required." })
        .length([2, 16], { message: "Prefix must be between 2 and 16 characters long." })
        .truthy(
          blocklist.rules.filter(
            (_rule) => _rule.prefix === value.prefix && _rule.direction === value.direction && _rule.id !== value.id,
          ).length === 0,
          { message: "Another rule with these settings already exists." },
        )
        .pattern("^[+]{1}[1-9]{1}[0-9X]{0,14}", {
          message: 'The prefix must start with "+" followed by a number between 1 and 9.',
        }),
    }),
    onSubmit: () => Api.storeBlocklistRule(blocklist, value),
    onSuccess: (response) => {
      if (!value.id) {
        dispatch({ type: "id", value: response.data.rule.id });
        blocklistDispatch({ type: "addRule", rule: { ...value, id: response.data.rule.id } });
        setActiveRule({ ...value, id: response.data.rule.id });
      } else {
        blocklistDispatch({ type: "updateRule", rule: value });
      }
    },
  });

  const formHandler = (property, value) => {
    dispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  return (
    <EditorForm
      title="Rule settings"
      action={<SaveIndicator formState={formState} errors={allErrors} save={save} mini />}
    >
      {isPbxBlocklist && (
        <InputSelect
          id="direction"
          label="Direction"
          onChange={formHandler}
          value={value.direction}
          selectValue="value"
          selects={[
            { value: "in", title: "Incoming" },
            { value: "out", title: "Outgoing" },
          ]}
          getOptionLabel={(option) => option.title}
          error={errors}
        />
      )}
      <InputPhone id="prefix" label="Prefix" onChange={formHandler} value={value.prefix} error={errors} />
      <InputText
        id={`description`}
        label="Description"
        name="description"
        onChange={formHandler}
        value={value.description}
        error={errors}
      />
      <InputToggle
        id="allowed"
        onChange={formHandler}
        states={[false, true]}
        value={value.allowed}
        primary="Allow"
        secondary="Whether to allow or block matches to this prefix."
        error={errors}
      />
    </EditorForm>
  );
}

RuleForm.propTypes = {
  blocklist: PropTypes.object,
  activeRule: PropTypes.object,
  blocklistDispatch: PropTypes.func,
  setActiveRule: PropTypes.func,
};

export default RuleForm;
